import { isNull, schPairedSalesCreateRequest } from '@aloft/toolkit-shared';

import { useRef } from 'react';
import { Navigate, useLocation, useNavigate, useParams } from 'react-router-dom';

import { usePropertyCoordinates } from '../../hooks/usePropertyCoordinates.js';
import { useToaster } from '../../hooks/useToaster/useToaster.js';
import { AdjustPairedSalesStandaloneModule } from '../../modules/adjustPairedSalesStandalone/AdjustPairedSalesStandaloneModule.js';
import {
  AdjustPairedSalesStandaloneModuleProps,
  StudyOutput,
} from '../../modules/adjustPairedSalesStandalone/types.js';
import { isPairedSalesCustomAnalysisFeature } from '../../modules/adjustPairedSalesStandalone/utils/isPairedSalesCustomAnalysisFeature.js';
import { formatReferenceViewID } from '../../modules/referenceView/referenceViewID/formatReferenceViewID.js';
import { AddStudy, schCreateStudy } from '../../modules/studies/hooks/useAddStudyForm.js';
import { getFallbackCenterCoordinates } from '../../utils/getFallbackCenterCoordinates.js';
import { findDisplayFormatForTerm } from '../adjust/schema/findDisplayFormatForTerm.js';
import { gridGeneric } from '../adjust/schema/gridGeneric.js';
import { useMetadataByID } from '../hooks/useMetadataByID.js';
import { useStudiesState } from '../hooks/useStudiesState.js';
import { useTkv2OrderID } from '../hooks/useTkv2OrderID.js';
import { getInitialStudy } from '../pairedSales/getInitialStudy.js';

const studiesRelativePath = '../../studies';

export function PairedSalesStandaloneRouteElement() {
  const makeToast = useToaster();
  const hasClearedOldMapState = useRef(false);

  const { createStudyReq, updateStudyReq, studies, selectedStudies, setSelectedStudies } =
    useStudiesState();

  const navigate = useNavigate();

  const orderID = useTkv2OrderID();
  const subjectMetadataByID = useMetadataByID()[formatReferenceViewID({ type: 'subject' })];
  const subjectPinCoordinates = usePropertyCoordinates(
    subjectMetadataByID ?? { address: undefined, listingID: undefined },
  );

  const modalInput = useLocation().state as AddStudy | null;
  const { studyID } = useParams();

  if (!!modalInput && !schCreateStudy.safeParse(modalInput).success) {
    return <Navigate to={studiesRelativePath} replace state={{}} />;
  }

  if (!hasClearedOldMapState.current) {
    // clear out any old map state hanging around
    window.localStorage.removeItem('paired-sales-map');
    hasClearedOldMapState.current = true;
  }

  const initialStudy = getInitialStudy(modalInput, studyID, studies.data);
  const { analysisFeature } = initialStudy.input;

  const createStudy = async (study: StudyOutput) => {
    try {
      const studyRequest = {
        body: study.body,
        input: study.input,
      };

      schPairedSalesCreateRequest.parse(studyRequest);

      const res = await createStudyReq.mutateAsync({ body: studyRequest });

      if (!selectedStudies.includes(res.id)) {
        setSelectedStudies([...selectedStudies, res.id]);
      }

      return res;
    } catch (err) {
      console.warn(err);
      makeToast({
        type: 'error',
        id: 'create-study-error',
        title: 'Error creating study',
      });
    }
  };

  const updateStudy = async (study: StudyOutput) => {
    if (!studyID || !orderID) {
      throw new Error('Cannot update study without studyID and orderID');
    }
    try {
      const studyRequest = {
        body: study.body,
        input: study.input,
      };

      const { body } = schPairedSalesCreateRequest.parse(studyRequest);

      const res = await updateStudyReq.mutateAsync({
        pathParams: { studyID },

        body: { body },
      });

      if (!selectedStudies.includes(res.id)) {
        setSelectedStudies([...selectedStudies, res.id]);
      }

      return res;
    } catch (err) {
      console.warn(err);
      makeToast({
        type: 'error',
        id: 'update-study-error',
        title: 'Error updating study',
      });
    }
  };

  const props: AdjustPairedSalesStandaloneModuleProps = {
    initialStudy,
    isEditing: isNull(modalInput),
    mapProps: {
      subject: subjectPinCoordinates.coordinates,
      center: subjectPinCoordinates.coordinates ?? getFallbackCenterCoordinates(),
      zoom: 14,
    },
    getDisplayValue: isPairedSalesCustomAnalysisFeature(analysisFeature)
      ? () => ''
      : findDisplayFormatForTerm(gridGeneric, analysisFeature),
    onCancel: () => navigate(studiesRelativePath),
    onSave: async (study) => {
      if (studyID) {
        await updateStudy(study);
      } else {
        await createStudy(study);
      }
      return;
    },
    onFinish: () => navigate(studiesRelativePath),
  };

  const loading = subjectPinCoordinates.isLoading || studies.isLoading;

  if (loading) {
    return null;
  }

  return <AdjustPairedSalesStandaloneModule {...props} />;
}
